/**
 * Signup Wizard Container
 *
 * Main container component for the multi-step signup wizard.
 * Manages step navigation, URL state, and renders appropriate step component.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Alert from '@/components/ui/Alert';
import { WizardProvider, useWizard } from './wizard/WizardContext';
import WizardProgress from './wizard/WizardProgress';
import Step1Terms from './wizard/Step1Terms';
import Step2Method from './wizard/Step2Method';
import Step3Credentials from './wizard/Step3Credentials';
import { useWizardAnalytics } from '@/hooks/useWizardAnalytics';
import { validateRedirectUrl } from '@/utils/redirect';

/**
 * Email verification success screen
 */
function RegistrationSuccess({ email }: { email: string }) {
  return (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
        <svg
          className="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
      <p className="text-gray-600 mb-4">
        We sent a verification link to{' '}
        <strong className="font-medium">{email}</strong>
      </p>
      <p className="text-sm text-gray-500 mb-6">
        Click the link in the email to verify your account and choose your password.
        The link will expire in 24 hours.
      </p>

      <div className="space-y-4">
        <Alert variant="info">
          Did not receive the email? Check your spam folder or wait a few minutes.
        </Alert>

        <div className="pt-2">
          <Link
            to="/login"
            className="inline-flex w-full justify-center items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Inner wizard content (wrapped by WizardProvider)
 */
function WizardContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { state, setStep, goToNextStep, goToPreviousStep, canProceedToStep } = useWizard();
  const analytics = useWizardAnalytics();

  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const currentStep = state.currentStep;

  // Track wizard start on mount
  useEffect(() => {
    analytics.trackWizardStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL with current step (one-way: state → URL)
  // Use a ref to track if we're currently updating to prevent loops
  useEffect(() => {
    const currentUrlStep = searchParams.get('step');
    if (currentUrlStep !== String(currentStep)) {
      setSearchParams({ step: String(currentStep) }, { replace: true });
    }
    // Only depend on currentStep to avoid loops from searchParams changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const step = searchParams.get('step');
      if (step) {
        const stepNum = parseInt(step, 10) as 1 | 2 | 3;
        if ([1, 2, 3].includes(stepNum) && canProceedToStep(stepNum)) {
          setStep(stepNum);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [searchParams, canProceedToStep, setStep]);

  // Track abandonment on unmount
  useEffect(() => {
    return () => {
      // Only track abandonment if not completed
      if (!registeredEmail) {
        analytics.trackAbandoned(currentStep);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Store redirect parameter for post-verification
  useEffect(() => {
    const redirectParam = searchParams.get('redirect');
    const validatedRedirect = validateRedirectUrl(redirectParam);
    if (validatedRedirect) {
      localStorage.setItem('postAuthRedirect', validatedRedirect);
    }
  }, [searchParams]);

  /**
   * Handle step 1 completion
   */
  const handleStep1Next = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  /**
   * Handle step 2 completion (email method selected)
   */
  const handleStep2Next = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  /**
   * Handle step 2 back
   */
  const handleStep2Back = useCallback(() => {
    goToPreviousStep();
  }, [goToPreviousStep]);

  /**
   * Handle step 3 completion (registration successful)
   */
  const handleStep3Complete = useCallback((email: string) => {
    setRegisteredEmail(email);
  }, []);

  /**
   * Handle step 3 back
   */
  const handleStep3Back = useCallback(() => {
    goToPreviousStep();
  }, [goToPreviousStep]);

  // Show success screen after registration
  if (registeredEmail) {
    return <RegistrationSuccess email={registeredEmail} />;
  }

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <WizardProgress currentStep={currentStep} />

      {/* Step content */}
      <div className="mt-6">
        {currentStep === 1 && <Step1Terms onNext={handleStep1Next} />}
        {currentStep === 2 && (
          <Step2Method onNext={handleStep2Next} onBack={handleStep2Back} />
        )}
        {currentStep === 3 && (
          <Step3Credentials onComplete={handleStep3Complete} onBack={handleStep3Back} />
        )}
      </div>

      {/* Sign in link */}
      <p className="mt-6 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}

/**
 * SignupWizard Component
 *
 * Main export - wraps content with WizardProvider
 */
export default function SignupWizard() {
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const initialStep = stepParam
    ? (parseInt(stepParam, 10) as 1 | 2 | 3)
    : undefined;

  return (
    <WizardProvider initialStep={initialStep}>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
          Create your account
        </h1>
        <WizardContent />
      </div>
    </WizardProvider>
  );
}
